import { FC, useState } from 'react';
import { ClipboardIcon, CheckIcon } from '@heroicons/react/outline';
import type { WidgetStyle } from './WidgetStylePicker';

interface EmbedCodeSnippetProps {
  providerId: string;
  style: WidgetStyle;
  baseUrl: string;
}

const EmbedCodeSnippet: FC<EmbedCodeSnippetProps> = ({
  providerId,
  style,
  baseUrl,
}) => {
  const [copied, setCopied] = useState(false);

  const snippet = `<script src="${baseUrl}/widget/client-login.js"\n  data-provider-id="${providerId}"\n  data-style="${style}"></script>`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(snippet);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard access denied or unsupported — don't show "Copied"
    }
  };

  const tag = 'text-[#7aa2f7]'; // blue — tags
  const attr = 'text-[#bb9af7]'; // purple — attribute names
  const val = 'text-[#9ece6a]'; // green — string values

  return (
    <div>
      <div className="mb-1 text-caption font-medium text-green-150">
        Embed code
      </div>
      <div className="mb-3 text-caption text-neutral-125">
        Copy and paste this snippet into your website&apos;s HTML where you want
        the widget to appear.
      </div>
      <div className="relative rounded-lg bg-[#1a1b2e] p-4">
        <pre className="font-mono whitespace-pre-wrap text-[11px] leading-relaxed">
          <span className={tag}>&lt;script</span>{' '}
          <span className={attr}>src</span>
          <span className="text-white">=</span>
          <span className={val}>
            &quot;{baseUrl}/widget/client-login.js&quot;
          </span>
          {'\n  '}
          <span className={attr}>data-provider-id</span>
          <span className="text-white">=</span>
          <span className={val}>&quot;{providerId}&quot;</span>
          {'\n  '}
          <span className={attr}>data-style</span>
          <span className="text-white">=</span>
          <span className={val}>&quot;{style}&quot;</span>
          <span className={tag}>&gt;&lt;/script&gt;</span>
        </pre>
        <button
          type="button"
          onClick={handleCopy}
          className="text-xs absolute top-2.5 right-2.5 flex items-center gap-1 rounded-md border border-white/20 bg-white/10 px-2.5 py-1 text-neutral-75 transition-colors hover:bg-white/20"
        >
          {copied ? (
            <>
              <CheckIcon className="h-3.5 w-3.5" />
              Copied
            </>
          ) : (
            <>
              <ClipboardIcon className="h-3.5 w-3.5" />
              Copy
            </>
          )}
        </button>
      </div>
      <div className="mt-2 text-caption text-neutral-110">
        The widget will automatically use your branding colors and logo, and
        inherit your site&apos;s fonts and text color so it matches the page it
        sits on.
      </div>
    </div>
  );
};

export default EmbedCodeSnippet;
